import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FadeIn, StaggeredGrid } from "@/components/animated";
import { Card, CardContent, Badge, Skeleton } from "@learn-me/ui";
import { formatCurrency } from "@learn-me/utils";
import { FileText, Download, ArrowRight } from "lucide-react";
import {
  materialsApi,
  MATERIAL_CATEGORY_LABELS,
} from "@/features/materials/api/materialsApi";
import { normalizeImageUrl } from "@/lib/imageUrl";

export function MaterialsSection() {
  const { data: materials, isLoading } = useQuery({
    queryKey: ["home-materials"],
    queryFn: () => materialsApi.listPublic(),
  });

  if (isLoading) {
    return (
      <section className="py-20 bg-secondary-50/50">
        <div className="container">
          <div className="text-center mb-12">
            <Skeleton className="h-8 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-72 w-full" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!materials || materials.length === 0) {
    return null;
  }

  const featured = materials.slice(0, 6);

  return (
    <section className="py-20 bg-secondary-50/50">
      <div className="container">
        <FadeIn>
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-4">
              Materiales digitales
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-3">
              Recursos descargables para profundizar
            </h2>
            <p className="text-secondary-500 max-w-2xl mx-auto">
              Lecturas, prácticas, manuales y guías que puedes adquirir de forma
              independiente o como complemento de tus cursos.
            </p>
          </div>
        </FadeIn>

        <StaggeredGrid
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          staggerDelay={0.1}
        >
          {featured.map((m) => {
            const list = m.listPrice != null ? Number(m.listPrice) : null;
            const sale = Number(m.salePrice);
            const showDiscount =
              !m.isFree && m.hasDiscount && list != null && list > sale;
            return (
              <Link key={m.id} to={`/materiales/${m.slug}`}>
                <motion.div
                  whileHover={{ y: -6, transition: { duration: 0.2 } }}
                  className="h-full"
                >
                  <Card className="h-full overflow-hidden border-0 shadow-md hover:shadow-premium transition-all">
                    <div className="aspect-video bg-secondary-100 relative">
                      {m.coverImageUrl ? (
                        <img
                          src={normalizeImageUrl(m.coverImageUrl) ?? ""}
                          alt={m.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <FileText className="h-14 w-14 text-secondary-400" />
                        </div>
                      )}
                      <Badge
                        className="absolute top-3 left-3"
                        variant="outline"
                      >
                        {MATERIAL_CATEGORY_LABELS[m.category]}
                      </Badge>
                      {showDiscount && (
                        <Badge className="absolute top-3 right-3 bg-red-500 text-white border-0">
                          {Math.round(((list! - sale) / list!) * 100)}% OFF
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-secondary-900 line-clamp-2 mb-2">
                        {m.title}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="flex items-baseline gap-2">
                          {showDiscount && (
                            <span className="text-xs text-secondary-400 line-through">
                              {formatCurrency(list!, m.currency)}
                            </span>
                          )}
                          <span className="font-bold text-secondary-900">
                            {m.isFree
                              ? "Gratis"
                              : formatCurrency(sale, m.currency)}
                          </span>
                        </span>
                        <Download className="h-4 w-4 text-secondary-400" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </Link>
            );
          })}
        </StaggeredGrid>

        <div className="text-center mt-10">
          <Link
            to="/materiales"
            className="inline-flex items-center text-primary-600 font-medium hover:underline"
          >
            Ver todos los materiales
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
